<script setup lang="ts">
import {onMounted, ref, watch} from "vue";
import {cityApi, disruptionLogisticsApi, wholesalerApi} from "@/container";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import dayjs from "dayjs";
import {Wholesaler} from "@/disruption-logistics/interfaces/wholesalers";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import Button from "../../core/components/ui/button/Button.vue";
import {PharmaMLOrder} from "@/disruption-logistics/interfaces/availability-providers";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import {Department} from "@/core/interfaces/City";
import {CircleCheck, CircleX} from "@lucide/vue";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {AcceptableValue} from "reka-ui";
import {ProductSearchQueryParams} from "@/auto-complete-searchbar/interfaces/ISearchBarAutoComplete";

const props = defineProps<{ filters: ProductSearchQueryParams, displayProduct?: boolean; requestToken: string }>();

const orders = ref<PharmaMLOrder[]>([]);
const wholesalers = ref<Wholesaler[]>([]);
const from = ref<number>(0);
const loading = ref(false);
const loadingMore = ref(false);
const hasMore = ref(true);
const selectedWholesalers = ref<AcceptableValue[]>();
const selectedDepartments = ref<AcceptableValue[]>();
const departments = ref<Department[]>([]);

const headers = [
    {
        title: "Date",
        key: "date",
        value: (item) => displayDate(item.date)
    },
    {
        title: "Pharmacie (département)",
        key: "department",
        value: (item) => item.department ? `Pharmacie (${item.department})` : "Pharmacie"
    },
    {
        title: "Grossiste",
        key: "wholesaler.name",
        value: (item) => item.wholesaler.name ?? "-"
    },
    {
        title: "Nom du produit",
        key: "product.name",
        value: (item) => item.product.name ?? "-"
    },
    {
        title: "Commandé / Livré",
        key: "quantity_available",
    },
    {
        title: "Code erreur",
        key: "code",
        value: (item) => {
            if (item.quantity_delivered < item.quantity_ordered) {
                return (item?.code && item?.message) ? `${item.code} - ${item.message}` : 'Produit non disponible';
            }

            return "";
        }
    }
];

/**
 * On mount, fetch the 20 first orders with disruption product availability for the given product id.
 */
onMounted(async () => {
    loading.value = true;
    await fetchOrders();
    wholesalers.value = await wholesalerApi().getMainWholesaler();
    departments.value = await cityApi().getDepartments();
    loading.value = false;
});

watch(() => props.filters, async () => {
    loading.value = true;
    await fetchOrders(selectedWholesalers.value as number[] | undefined, selectedDepartments.value ?? undefined);
    loading.value = false;
});


watch(selectedDepartments, async () => {
    loading.value = true;
    await fetchOrders(selectedWholesalers.value as number[] | undefined, selectedDepartments.value ?? undefined);
    loading.value = false;
});

watch(selectedWholesalers, async () => {
    loading.value = true;
    await fetchOrders(selectedWholesalers.value as number[] | undefined, selectedDepartments.value ?? undefined);
    loading.value = false;
});

/**
 * Fetch orders from the API.
 */
const fetchOrders = async (wholesalerIds?: number[], departmentCodes?: AcceptableValue[]): Promise<void> => {
    const response = await disruptionLogisticsApi().disruptionProductPharmaML(props.filters, props.requestToken, 0, wholesalerIds, departmentCodes as string[] | undefined);
    orders.value = response.data;
    from.value = response.meta?.from ?? 0;
    if (response.meta?.total && response.meta.total <= from.value) {
        hasMore.value = false;
    }
};

/**
 *  Fetch more orders from the API.
 */
const fetchMoreOrders = async (wholesalerIds?: number[], departmentCodes?: AcceptableValue[]): Promise<void> => {
    const response = await disruptionLogisticsApi().disruptionProductPharmaML(props.filters, props.requestToken, from.value, wholesalerIds, departmentCodes as string[] | undefined);
    orders.value = [...orders.value, ...response.data];
    from.value = response.meta?.from ?? 0;
    if (response.meta?.total && response.meta.total <= from.value) {
        hasMore.value = false;
    }
};

/**
 * Load more orders from the API.
 */
const loadMore = async () => {
    loadingMore.value = true;
    await fetchMoreOrders(selectedWholesalers.value as number[] | undefined, selectedDepartments.value ?? undefined);
    loadingMore.value = false;
};

/**
 * Display hour if the date is today, the full date otherwise.
 * @param date
 */
const displayDate = (date: string) => {
    if (new Date(date).toDateString() === new Date().toDateString()) {
        return dayjs(date).format('HH:mm');
    }

    if (new Date(date).getFullYear() === new Date().getFullYear()) {
        return dayjs(date).format('DD/MM HH:mm');
    }

    return dayjs(date).format('DD/MM/YYYY HH:mm');
};

/**
 * Get department name based on department code.
 */
const departmentCodeMapper = (code?: number) => {
    if (code) {
        return departments.value.find((department: Department) => department.code === code.toString())?.name;
    }

    return "Département non renseigné";
};
</script>

<template>
  <div
    class="bg-white rounded-md shadow p-3 flex flex-col gap-6"
  >
    <h2>Derniers pharmaML</h2>
    <div v-if="loading">
      <base-spinner size="button" />
    </div>
    <div v-else>
      <div class="flex flex-row flex-wrap gap-4 pb-4">
        <Select
          v-model="selectedDepartments"
          multiple
        >
          <SelectTrigger class="flex flex-row justify-between w-64">
            <SelectValue placeholder="Départements" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="department in departments"
              :key="department.id"
              :value="department.code"
            >
              <div class="flex items-center">
                <span>{{ department.code }} {{ department.name }}</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
        <Select v-model="selectedWholesalers" multiple>
          <SelectTrigger class="w-64">
            <SelectValue placeholder="Grossistes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="wholesaler in wholesalers" :key="wholesaler.id"
              :value="wholesaler.id.toString()"
            >
              <div class="flex items-center">
                <span>{{ wholesaler.name }}</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <v-data-table
        :headers="headers"
        hide-default-footer
        :item-value="item => item.id"
        :items="orders"
        :items-per-page="-1"
        :loading="loading || loadingMore"
      >
        <template #[`item.department`]="{ item, value }">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <p>{{ value }}</p>
              </TooltipTrigger>
              <TooltipContent class="bg-primary font-medium">
                <p>{{ departmentCodeMapper(item.department) }}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </template>
        <template #[`item.quantity_available`]="{ item }">
          <div class="flex flex-row items-center gap-2">
            <p>{{ item.quantity_ordered }} / {{ item.quantity_delivered }}</p>
            <CircleX v-if="item.quantity_delivered < item.quantity_ordered" class="w-5 h-5 text-red-500" />
            <CircleCheck v-else class="w-5 h-5 text-secondary" />
          </div>
        </template>
      </v-data-table>
      <div
        v-if="hasMore"
        class="flex justify-center"
      >
        <Button
          variant="outline"
          :disabled="loadingMore || !hasMore"
          class="max-w-md mx-auto z-20"
          @click="loadMore"
        >
          <BaseFlexSpinner v-if="loadingMore" class="w-4 h-4" />
          <span v-else>Voir plus</span>
        </Button>
      </div>
      <div v-else class="flex flex-1 justify-center pt-2">
        <span class="font-semibold">Vous avez atteint la fin</span>
      </div>
    </div>
  </div>
</template>
